import axios from "axios";
import config from "../../../config";
import { getAuthToken } from "../../../utils/authStorage";

const authConfig = (extra = {}) => {
  const token = getAuthToken();
  return {
    ...extra,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  };
};

const base = () => `${config.apiBaseUrl}/hpc/admin/promotions`;

export const PROMOTION_TYPE_OPTIONS = [
  { value: "PERCENTAGE", label: "Percentage off membership fee" },
  { value: "FIXED", label: "Fixed LKR off membership fee" },
  { value: "SPECIAL_PRICE", label: "Special membership price" },
  { value: "ADMISSION_WAIVER", label: "Admission fee waiver" },
];

export const PACKAGE_CATEGORY_OPTIONS = [
  { value: "individual", label: "Individual" },
  { value: "couple", label: "Couple" },
  { value: "family", label: "Family" },
];

export const DURATION_OPTIONS = [
  { value: "monthly", label: "Monthly" },
  { value: "three_months", label: "3 Months" },
  { value: "six_months", label: "6 Months" },
  { value: "annual", label: "Annual" },
];

// Eligibility rows the admin can tick. Couple is adult only; Family has no member type.
export const ELIGIBILITY_ROWS = [
  { package_category: "individual", member_type: "adult", label: "Individual – Adult" },
  { package_category: "individual", member_type: "student", label: "Individual – Student" },
  { package_category: "couple", member_type: "adult", label: "Couple" },
  { package_category: "family", member_type: null, label: "Family" },
];

export const STATUS_FILTERS = [
  { value: "", label: "All" },
  { value: "DRAFT", label: "Draft" },
  { value: "ACTIVE", label: "Active / Scheduled / Expired" },
  { value: "SCHEDULED", label: "Scheduled" },
  { value: "EXPIRED", label: "Expired" },
  { value: "DISABLED", label: "Disabled" },
];

export const typeLabel = (value) =>
  PROMOTION_TYPE_OPTIONS.find((o) => o.value === value)?.label || value || "—";

export const durationLabel = (value) =>
  DURATION_OPTIONS.find((o) => o.value === value)?.label || value || "—";

export const valueLabel = (promotion) => {
  if (!promotion) return "—";
  const value = Number(promotion.discount_value || 0);
  switch (promotion.promotion_type) {
    case "PERCENTAGE":
      return `${value}%`;
    case "FIXED":
      return `LKR ${value.toLocaleString("en-LK")} off`;
    case "SPECIAL_PRICE":
      return `LKR ${value.toLocaleString("en-LK")}`;
    case "ADMISSION_WAIVER":
      return "Admission waived";
    default:
      return String(value);
  }
};

export const statusChipColor = (status) =>
  ({
    ACTIVE: "success",
    SCHEDULED: "info",
    EXPIRED: "default",
    DRAFT: "warning",
    DISABLED: "error",
    PENDING: "warning",
    REDEEMED: "success",
    CANCELLED: "default",
    REVERSED: "error",
  }[status] || "default");

export const errorMessage = (err, fallback) => {
  const data = err?.response?.data;
  if (!data) return fallback;
  const details = data.errors ? Object.values(data.errors).join(" ") : "";
  return [data.error, details].filter(Boolean).join(" ") || fallback;
};

export const listPromotions = async (params) =>
  (await axios.get(base(), authConfig({ params }))).data.promotions || [];

export const getPromotion = async (id) => (await axios.get(`${base()}/${id}`, authConfig())).data;

export const createPromotion = async (payload) => (await axios.post(base(), payload, authConfig())).data;

export const updatePromotion = async (id, payload) =>
  (await axios.put(`${base()}/${id}`, payload, authConfig())).data;

export const setPromotionEnabled = async (id, enabled, reason) =>
  (await axios.post(`${base()}/${id}/${enabled ? "enable" : "disable"}`, { reason }, authConfig())).data;

export const duplicatePromotion = async (id, code) =>
  (await axios.post(`${base()}/${id}/duplicate`, code ? { code } : {}, authConfig())).data;

export const reverseRedemption = async (redemptionId, reason) =>
  (await axios.post(`${base()}/redemptions/${redemptionId}/reverse`, { reason }, authConfig())).data;

export const reconcileRedemption = async (redemptionId, action, note) =>
  (await axios.post(`${base()}/redemptions/${redemptionId}/reconcile`, { action, note }, authConfig())).data;

export const getPromotionReport = async (params) =>
  (await axios.get(`${base()}/report`, authConfig({ params }))).data.report;

export const downloadPromotionReport = async (params) => {
  const response = await axios.get(`${base()}/report/export`, authConfig({ params, responseType: "blob" }));
  const url = window.URL.createObjectURL(response.data);
  const link = document.createElement("a");
  const disposition = response.headers?.["content-disposition"] || "";
  const match = disposition.match(/filename="?([^";]+)"?/);
  link.href = url;
  link.download = match ? match[1] : "hpc-promotions.csv";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};
